'use client'

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

interface HeroLowerHeaderScrollProps {
  headline: string
  children: ReactNode
}

export const HeroLowerHeaderScroll = ({ headline, children }: HeroLowerHeaderScrollProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [scrollProgress, setScrollProgress] = useState(0)
  const words = headline.split(' ')
  const wordsCount = words.length

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      const windowHeight = window.innerHeight
      const startPoint = windowHeight
      const endPoint = windowHeight * 0.75 - rect.height
      const totalRange = startPoint - endPoint
      const progress = Math.max(0, Math.min(1, (startPoint - rect.top) / totalRange))
      setScrollProgress(progress)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const calculateWidth = useCallback(
    (index: number) => {
      const step = 1 / wordsCount
      if (scrollProgress >= index * step) return '100%'
      if (scrollProgress >= (index - 1) * step) return `${((scrollProgress - (index - 1) * step) / step) * 100}%`
      return '0%'
    },
    [scrollProgress, wordsCount]
  )

  const spanClassName = 'absolute left-0 top-0 h-full overflow-hidden whitespace-nowrap text-navy'

  return (
    <div className="flex items-start justify-between self-stretch flex-col gap-[40px] lg:flex-row lg:gap-0">
      <div
        ref={containerRef}
        className="text-navy/20 desktop-xl:max-w-[1140px] desktop-xl:text-[64px] desktop-xl:font-medium desktop-xl:leading-[1.2] desktop-xl:tracking-[-1.92px] max-360:text-[32px] h-auto max-w-full text-[37px] leading-[1.3] font-normal tracking-[-0.96px] not-italic lg:max-w-[850px] lg:text-[48px] lg:tracking-[-1.44px]"
      >
        {words.map((word, index) => (
          <div key={index} className="relative inline-block">
            {word}
            <span style={{ width: calculateWidth(index + 1) }} className={spanClassName}>
              {word}&nbsp;
            </span>
            &nbsp;
          </div>
        ))}
      </div>
      <div
        className="ml-0 flex max-w-[360px] flex-col items-start lg:ml-5 desktop-xl:max-w-[452px] max-360:max-w-full"
        style={{ opacity: scrollProgress }}
      >
        {children}
      </div>
    </div>
  )
}

interface HeroLowerCardsScrollProps {
  children: ReactNode
}

export const HeroLowerCardsScroll = ({ children }: HeroLowerCardsScrollProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [opacity, setOpacity] = useState(0)

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      const windowHeight = window.innerHeight
      const startPosition = windowHeight
      const endPosition = windowHeight * 0.5
      const progress = Math.max(0, Math.min(1, (startPosition - rect.top) / (startPosition - endPosition)))
      setOpacity(progress)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <div ref={containerRef} style={{ opacity }} className="flex flex-col justify-start gap-[32px] max-xs:gap-[24px]">
      {children}
    </div>
  )
}
